import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SettingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Segmented } from 'antd';
import dayjs from 'dayjs';
import { escapeCsvField, downloadCsv } from '../utils/csv';
import { Link, useNavigate } from 'react-router-dom';
import useColumnConfig from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import CollectionPointPassagesExport from '../components/CollectionPointPassagesExport';
import {
  listCollectionPointsForPartner,
  getMyCollectionPoints,
  createCollectionPoint,
  updateCollectionPoint,
  deleteCollectionPoint,
} from '../modules/collection-points/api';
import { listSitesForPartner } from '../modules/sites/api';

// Comma/dot decimal parser (Swiss operators type "0,5"). Mirrors the
// pickup-weight modal (see CLAUDE.md "comma + zero handling").
const numParser = (v) =>
  v == null
    ? ''
    : String(v)
        .replace(/,/g, '.')
        .replace(/[^\d.]/g, '');
const numFormatter = (v) => (v == null || v === '' ? '' : String(v));

const CP_FIXED_KEYS = ['name', 'actions'];

// OVO-206 — first-class Collection Points ("Caisses") page. Lists every box
// for the partner across all Sites, with full CRUD. A box belongs to exactly
// one Site (its physical address); capacity drives the OVO-170 fill-rate
// suggestions. Rendered standalone at /admin/collection-points (admin) via
// AdminCatalogPage; `viewOnly` makes it read-only for a future partner-viewer
// page (OVO-002 — backend already exposes /partners/me/collection-points).
export default function PartnerCollectionPoints({ partner, viewOnly = false }) {
  const partnerId = partner?.id;
  const [points, setPoints] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const navigate = useNavigate();
  // CT prod readiness (Aug 2026): flat list vs the "par localité" grouping.
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const reload = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const ptsRes = viewOnly ? await getMyCollectionPoints() : await listCollectionPointsForPartner(partnerId);
      setPoints(Array.isArray(ptsRes.response) ? ptsRes.response : []);
      if (!viewOnly) {
        const sitesRes = await listSitesForPartner(partnerId);
        setSites(Array.isArray(sitesRes.response) ? sitesRes.response : []);
      }
    } catch (e) {
      message.error(e?.message || 'Failed to load collection points');
    } finally {
      setLoading(false);
    }
  }, [partnerId, viewOnly]);

  useEffect(() => {
    reload();
  }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return points;
    return points.filter((p) =>
      [p.name, p.externalRef, p.site?.name, p.site?.address]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [points, search]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isArchived: false });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    const payload = {
      name: values.name?.trim(),
      externalRef: values.externalRef?.trim() || null,
      capacityKg: values.capacityKg == null ? null : Number(values.capacityKg),
      minKg: values.minKg == null ? null : Number(values.minKg),
      notes: values.notes ?? null,
      isArchived: !!values.isArchived,
    };
    const { error } = editing
      ? await updateCollectionPoint(editing.id, { siteId: values.siteId, ...payload })
      : await createCollectionPoint(partnerId, { siteId: values.siteId, ...payload });
    setSaving(false);
    if (error) {
      message.error(error.message || 'Save failed');
      return;
    }
    message.success(editing ? 'Collection point updated' : 'Collection point created');
    setEditorOpen(false);
    reload();
  };

  const handleDelete = async (id) => {
    const { error } = await deleteCollectionPoint(id);
    if (error) {
      message.error(error.message || 'Delete failed');
      return;
    }
    message.success('Collection point deleted');
    reload();
  };

  const columns = [
    {
      title: 'Box',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => String(a.name).localeCompare(String(b.name)),
      render: (name, r) => (
        <Space size={4}>
          {r.externalRef ? (
            <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
              {r.externalRef}
            </Tag>
          ) : null}
          <strong>{name}</strong>
          {r.isArchived ? <Tag>archived</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Site',
      key: 'site',
      ellipsis: true,
      render: (_, r) =>
        r.site ? (
          <Tooltip title={r.site.address}>
            {viewOnly ? (
              <span>{r.site.name}</span>
            ) : (
              <Link to={`/admin/partners/${partnerId}/sites?focusSiteId=${r.site.id}`}>{r.site.name}</Link>
            )}
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      title: 'Commune',
      key: 'commune',
      width: 140,
      sorter: (a, b) => String(a.site?.commune || '').localeCompare(String(b.site?.commune || '')),
      render: (_, r) => r.site?.commune || '—',
    },
    {
      title: 'Capacity (kg)',
      dataIndex: 'capacityKg',
      key: 'capacityKg',
      width: 120,
      sorter: (a, b) => (a.capacityKg ?? -1) - (b.capacityKg ?? -1),
      render: (v) => (v == null ? '—' : v),
    },
    {
      title: 'Min (kg)',
      dataIndex: 'minKg',
      key: 'minKg',
      width: 100,
      render: (v) => (v == null ? '—' : v),
    },
    {
      title: 'Orders',
      key: 'orders',
      width: 90,
      sorter: (a, b) => (a._count?.orders ?? 0) - (b._count?.orders ?? 0),
      render: (_, r) =>
        viewOnly ? (
          r._count?.orders ?? 0
        ) : (
          <Link to={`/admin/orders?partnerId=${partnerId}`}>{r._count?.orders ?? 0}</Link>
        ),
    },
    // #132 — open / total support tickets per box (operators only).
    ...(viewOnly
      ? []
      : [
          {
            title: 'Tickets',
            key: 'tickets',
            width: 100,
            sorter: (a, b) =>
              (a.ticketCounts?.open ?? 0) - (b.ticketCounts?.open ?? 0) ||
              (a.ticketCounts?.total ?? 0) - (b.ticketCounts?.total ?? 0),
            filters: [
              { text: 'With open tickets', value: 'open' },
              { text: 'With any ticket', value: 'any' },
              { text: 'No ticket', value: 'none' },
            ],
            onFilter: (value, r) => {
              const c = r.ticketCounts || { open: 0, total: 0 };
              if (value === 'open') return c.open > 0;
              if (value === 'any') return c.total > 0;
              return c.total === 0;
            },
            render: (_, r) => {
              const c = r.ticketCounts || { open: 0, total: 0 };
              if (!c.total) return <span style={{ color: 'var(--text-muted)' }}>0</span>;
              return (
                <Link to={`/admin/tickets?collectionPointId=${r.id}&resolved=all`}>
                  {c.open ? <Tag color="red">{c.open} open</Tag> : null}
                  <span style={{ color: 'var(--text-muted)' }}>{c.total}</span>
                </Link>
              );
            },
          },
        ]),
    ...(viewOnly
      ? []
      : [
          {
            title: '',
            key: 'actions',
            width: 100,
            render: (_, r) => (
              <Space size={4}>
                <Tooltip title="Open">
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/admin/collection-points/${r.id}`)}
                  />
                </Tooltip>
                <Popconfirm
                  title="Delete this collection point?"
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]),
  ];

  // Page size lives under the gear (house rule) — no bottom-right size changer.
  const columnConfig = useColumnConfig('collection-points-columns', columns, CP_FIXED_KEYS);

  // "Par localité" (CT ask): group by the site's commune, with subtotals.
  const byCommune = useMemo(() => {
    const groups = new Map();
    for (const p of filtered) {
      const key = p.site?.commune || '— (sans commune)';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(p);
    }
    return [...groups.entries()]
      .map(([commune, pts]) => ({
        commune,
        count: pts.length,
        active: pts.filter((x) => !x.isArchived).length,
        capacityKg: pts.reduce((t, x) => t + (x.capacityKg || 0), 0),
        orders: pts.reduce((t, x) => t + (x._count?.orders || 0), 0),
        points: pts,
      }))
      .sort((a, b) => a.commune.localeCompare(b.commune));
  }, [filtered]);

  const exportCsvParLocalite = () => {
    const lines = [
      ['Commune', 'Box', 'No', 'Site', 'Address', 'Capacity (kg)', 'Access', 'Container', 'Orders', 'Archived'].map(
        escapeCsvField,
      ),
      ...byCommune.flatMap((g) =>
        g.points.map((p) =>
          [
            g.commune,
            p.name,
            p.externalRef || '',
            p.site?.name || '',
            p.site?.address || '',
            p.capacityKg ?? '',
            p.accessType || '',
            p.containerType || '',
            p._count?.orders ?? 0,
            p.isArchived ? 'yes' : '',
          ].map(escapeCsvField),
        ),
      ),
    ];
    downloadCsv(lines, `caisses-par-localite-${dayjs().format('YYYY-MM-DD')}.csv`);
  };

  const communeColumns = [
    { title: 'Commune', dataIndex: 'commune', key: 'commune' },
    { title: 'Boxes', dataIndex: 'count', key: 'count', width: 90 },
    { title: 'Active', dataIndex: 'active', key: 'active', width: 90 },
    {
      title: 'Capacity (kg)',
      dataIndex: 'capacityKg',
      key: 'capacityKg',
      width: 120,
      render: (v) => (v ? Math.round(v) : '—'),
    },
    { title: 'Orders', dataIndex: 'orders', key: 'orders', width: 90 },
  ];

  return (
    <div className="partner-collection-points" style={{ padding: 16 }}>
      <Card
        size="small"
        title={`Collection points (Caisses)${points.length ? ` · ${points.length}` : ''}`}
        extra={
          <Space>
            <Input
              allowClear
              prefix={<UnorderedListOutlined />}
              placeholder="Search box / number / site"
              style={{ width: 260 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {!viewOnly && (
              <Segmented
                size="small"
                value={view}
                onChange={setView}
                options={[
                  { label: 'List', value: 'list' },
                  { label: 'Par commune', value: 'commune' },
                ]}
              />
            )}
            {!viewOnly && (
              <Tooltip title="Export the box inventory, grouped by commune (CSV)">
                <Button icon={<DownloadOutlined />} onClick={exportCsvParLocalite} disabled={!filtered.length} />
              </Tooltip>
            )}
            <CollectionPointPassagesExport partnerId={partnerId} viewOnly={viewOnly} partnerName={partner?.name} />
            {!viewOnly && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!partnerId}>
                Add box
              </Button>
            )}
            <Button type="text" icon={<SettingOutlined />} onClick={columnConfig.openDrawer} />
          </Space>
        }
      >
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>
          Physical boxes / containers collected at a Site. Capacity drives the fill-rate suggestions; several boxes can
          share one address.
        </div>
        {view === 'commune' && !viewOnly ? (
          <Table
            size="small"
            rowKey="commune"
            loading={loading}
            columns={communeColumns}
            dataSource={byCommune}
            pagination={false}
            expandable={{
              expandedRowRender: (g) => (
                <Table
                  size="small"
                  rowKey="id"
                  columns={columnConfig.visibleColumns}
                  dataSource={g.points}
                  pagination={false}
                  onRow={(r) => ({
                    onClick: () => navigate(`/admin/collection-points/${r.id}`),
                    style: { cursor: 'pointer' },
                  })}
                />
              ),
            }}
          />
        ) : (
          <Table
            size="small"
            rowKey="id"
            loading={loading}
            columns={columnConfig.visibleColumns}
            dataSource={filtered}
            onRow={
              viewOnly
                ? undefined
                : (r) => ({
                    onClick: (e) => {
                      // Guard the action cells (buttons/popconfirm) — row click opens the page.
                      if (e.target.closest('button, a, .ant-popover')) return;
                      navigate(`/admin/collection-points/${r.id}`);
                    },
                    style: { cursor: 'pointer' },
                  })
            }
            pagination={{
              pageSize: columnConfig.pageSize,
              showSizeChanger: false,
              hideOnSinglePage: true,
              showTotal: (t) => `${t} boxes`,
            }}
          />
        )}
        <ColumnConfigDrawer
          open={columnConfig.drawerOpen}
          onClose={columnConfig.closeDrawer}
          columns={columnConfig.orderedAllColumns}
          hidden={columnConfig.hidden}
          fixedKeys={CP_FIXED_KEYS}
          onToggle={columnConfig.toggleColumn}
          onMove={columnConfig.moveColumn}
          onReset={columnConfig.resetConfig}
          pageSize={columnConfig.pageSize}
          onPageSizeChange={columnConfig.setPageSize}
        />
      </Card>

      {!viewOnly && (
        <Modal
          title={editing ? `Edit collection point — ${editing.name}` : 'Add collection point'}
          open={editorOpen}
          onCancel={() => setEditorOpen(false)}
          onOk={handleSave}
          confirmLoading={saving}
          okText={editing ? 'Save' : 'Create'}
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="siteId"
              label="Site (address)"
              rules={[{ required: true, message: 'Pick the site this box is at' }]}
            >
              <Select
                showSearch
                placeholder="Select the site"
                optionFilterProp="label"
                options={sites
                  .filter((s) => !s.isArchived)
                  .map((s) => ({ value: s.id, label: `${s.name} — ${s.address}` }))}
              />
            </Form.Item>
            <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Required' }]}>
              <Input placeholder="e.g. Compost, Bac textile Nord, or the box number" />
            </Form.Item>
            <Form.Item name="externalRef" label="Box number (optional)">
              <Input placeholder="e.g. 7752" />
            </Form.Item>
            <div style={{ display: 'flex', gap: 12 }}>
              <Form.Item name="capacityKg" label="Capacity (kg)" style={{ flex: 1 }}>
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="e.g. 250"
                  parser={numParser}
                  formatter={numFormatter}
                />
              </Form.Item>
              <Form.Item name="minKg" label="Min (kg)" style={{ flex: 1 }}>
                <InputNumber min={0} style={{ width: '100%' }} parser={numParser} formatter={numFormatter} />
              </Form.Item>
            </div>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="isArchived" label="Archived" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
}
